import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { auth } from '../../firebase/firebase';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { determineRoleFromEmail, isEducationalEmail } from '../../utils/authChecks';
import { useSafetyCheck } from '../../hooks/useSafetyCheck';
import { containsHiddenProfanity } from '../../utils/userNameCheck';
 
const C = {
    primary:          '#6a37d4',
    primaryLight:     '#ae8dff',
    primaryContainer: '#ede8ff',
    secondary:        '#006947',
    secondaryContainer: '#d1fae5',
    surface:          '#f3f6ff',
    surfaceLow:       '#eaf1ff',
    surfaceHigh:      '#d6e3f9',
    onBackground:     '#252f3c',
    onSurface:        '#252f3c',
    onSurfaceVariant: '#525c6a',
    outlineVariant:   '#a4aebe',
};
 
const Signup: React.FC = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState<'account' | 'avatar' | 'goal'>('account');
    const [username, setUsername]             = useState('');
    const [email, setEmail]                   = useState('');
    const [password, setPassword]             = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [selectedAvatar, setSelectedAvatar] = useState('');
    const [selectedGoal, setSelectedGoal]     = useState('');
    const [error, setError]                   = useState('');
    const [loading, setLoading]               = useState(false);
 
    const avatars = ['🐱', '🐶', '🦊', '🐼', '🦁', '🐸', '🦄', '🐉', '🦖', '🚀', '⭐', '🌈'];
    const goals = [
        { id: 'write', emoji: '📝', label: 'Write for Fun',  desc: 'Create stories for enjoyment' },
        { id: 'share', emoji: '🌟', label: 'Share Stories',  desc: 'Connect with other writers' },
        { id: 'read',  emoji: '📖', label: 'Read Stories',   desc: 'Explore the community' },
    ];
 
    const steps = ['account', 'avatar', 'goal'];

    const { runPublishCheck } = useSafetyCheck();
    const [checking, setChecking] = useState(false);
 
    // ── Handlers (logic unchanged) ────────────────────────────────────────────
    const handleAccountSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        if (password !== confirmPassword) { setError('Passwords do not match'); return; }
        if (password.length < 6)          { setError('Password must be at least 6 characters'); return; }
        if (username.length < 3)          { setError('Username must be at least 3 characters'); return; }
        if (containsHiddenProfanity(username)) {
            setError("Please choose a different username. That one has a word we don't allow.");
            return;
        }
    
        setChecking(true);
        try {
            const safety = await runPublishCheck({ title: username.trim(), description: '' }, false);
    
            if (!safety.passed) {
                setError(safety.issues.map(i => i.detail).join(' '));
                return;
            }
            // A username goes live immediately and there's no review queue, so block it
            if (safety.needsReview) {
                setError("We couldn't approve that username. Please try a different one!");
                return;
            }
        } finally {
            setChecking(false);
        }
    
        setStep('avatar');
    };
 
    const handleAvatarSubmit = () => {
        if (!selectedAvatar) { setError('Please choose an avatar'); return; }
        setError('');
        setStep('goal');
    };
 
    const handleGoalSubmit = () => {
        if (!selectedGoal) { setError('Please choose a goal'); return; }
        setError('');
        handleFinalSubmit();
    };
 
    const handleFinalSubmit = async () => {
        setLoading(true);
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const role = determineRoleFromEmail(email);
            await updateProfile(userCredential.user, { displayName: username });
            await setDoc(doc(db, 'users', userCredential.user.uid), {
                uid: userCredential.user.uid,
                displayName: username,
                email,
                role,
                classes: [],
                isIndependent: true,
                avatar: selectedAvatar,
                goal: selectedGoal,
                createdAt: serverTimestamp(),
            });
            try {
                const { migrateGuestProgressToUser } = await import('../../contexts/ProgressContext');
                await migrateGuestProgressToUser(userCredential.user.uid);
            } catch (migrationError) {
                console.error('⚠️ Could not migrate guest progress:', migrationError);
            }
            navigate(role === 'teacher' ? '/teacher' : '/dashboard');
        } catch (err: any) {
            setError(err.message || 'Failed to create account');
        } finally {
            setLoading(false);
        }
    };
 
    // ── Shared styles ─────────────────────────────────────────────────────────
    const inputStyle: React.CSSProperties = {
        width: '100%', padding: '11px 14px',
        border: `1.5px solid ${C.outlineVariant}`,
        borderRadius: '10px', fontSize: '0.95rem',
        boxSizing: 'border-box',
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        background: C.surface, color: C.onSurface,
        outline: 'none', transition: 'border-color 0.15s',
    };
 
    const labelStyle: React.CSSProperties = {
        display: 'block', marginBottom: '6px',
        fontSize: '0.8rem', fontWeight: '700',
        color: C.onSurfaceVariant,
        fontFamily: "'Epilogue', sans-serif",
    };
 
    const primaryBtnStyle: React.CSSProperties = {
        width: '100%', padding: '14px',
        background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryLight} 100%)`,
        color: '#fff', border: 'none', borderRadius: '9999px',
        fontSize: '1rem', fontWeight: '700',
        cursor: 'pointer', marginBottom: '12px',
        fontFamily: "'Epilogue', sans-serif",
        boxShadow: `0 4px 14px ${C.primary}40`,
        transition: 'opacity 0.15s, transform 0.15s',
    };
 
    const ghostBtnStyle: React.CSSProperties = {
        width: '100%', padding: '12px',
        background: 'transparent',
        color: C.onSurfaceVariant,
        border: `1.5px solid ${C.outlineVariant}`,
        borderRadius: '9999px',
        fontSize: '0.9rem', fontWeight: '700',
        cursor: 'pointer',
        fontFamily: "'Epilogue', sans-serif",
        transition: 'border-color 0.15s, color 0.15s',
    };
 
    return (
        <div style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            background: C.surface,
            minHeight: '100vh',
            padding: '40px 20px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
            <div style={{
                background: '#fff',
                borderRadius: '24px',
                padding: '40px 36px',
                maxWidth: '480px',
                width: '100%',
                boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
                border: `1px solid ${C.surfaceHigh}`,
            }}>
                {/* Progress bar */}
                <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '32px' }}>
                    {steps.map((s, i) => (
                        <div key={s} style={{
                            height: '5px',
                            borderRadius: '9999px',
                            flex: 1,
                            background: steps.indexOf(step) >= i ? C.primary : C.surfaceHigh,
                            transition: 'background 0.3s ease',
                        }} />
                    ))}
                </div>
 
                {/* Role picker — step 1 only */}
                {step === 'account' && (
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '28px' }}>
                        <button
                            type="button"
                            style={{
                                flex: 1, padding: '14px',
                                border: `2px solid ${C.primary}`,
                                borderRadius: '12px',
                                background: C.primaryContainer,
                                color: C.primary,
                                fontSize: '0.95rem', fontWeight: '700',
                                cursor: 'pointer',
                                fontFamily: "'Epilogue', sans-serif",
                                transition: 'all 0.15s',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
                        >
                            👤 Student
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate('/signup-educator')}
                            style={{
                                flex: 1, padding: '14px',
                                border: `2px solid ${C.secondary}`,
                                borderRadius: '12px',
                                background: C.secondaryContainer,
                                color: C.secondary,
                                fontSize: '0.95rem', fontWeight: '700',
                                cursor: 'pointer',
                                fontFamily: "'Epilogue', sans-serif",
                                transition: 'all 0.15s',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
                        >
                            🎓 Educator
                        </button>
                    </div>
                )}
 
                {/* Step heading */}
                <h1 style={{
                    fontFamily: "'Epilogue', sans-serif",
                    fontSize: '1.8rem', fontWeight: '900',
                    color: C.onBackground,
                    margin: '0 0 6px 0', textAlign: 'center',
                }}>
                    {step === 'account' && 'Join Scriblish'}
                    {step === 'avatar'  && 'Choose Your Avatar'}
                    {step === 'goal'    && "What's Your Goal?"}
                </h1>
 
                {step === 'account' && (
                    <p style={{ textAlign: 'center', fontSize: '0.9rem', color: C.onSurfaceVariant, margin: '0 0 24px 0' }}>
                        Create stories, share ideas, make friends. It's free!
                    </p>
                )}
 
                {/* Error */}
                {error && (
                    <div style={{
                        background: '#fef2f2', border: '1px solid #fecaca',
                        borderRadius: '10px', padding: '12px',
                        marginBottom: '18px', color: '#dc2626',
                        fontSize: '0.875rem', textAlign: 'center', fontWeight: '500',
                    }}>
                        {error}
                    </div>
                )}
 
                {/* ── Step 1: Account info ──────────────────────────────────── */}
                {step === 'account' && (
                    <form onSubmit={handleAccountSubmit}>
                        {/* Username */}
                        <div style={{ marginBottom: '16px' }}>
                            <label style={labelStyle}>Username</label>
                            <input
                                type="text" value={username}
                                onChange={e => setUsername(e.target.value)}
                                placeholder="Choose a username" required
                                style={inputStyle}
                                onFocus={e => { e.target.style.borderColor = C.primary; }}
                                onBlur={e  => { e.target.style.borderColor = C.outlineVariant; }}
                            />
                            <div style={{ fontSize: '0.75rem', color: C.onSurfaceVariant, marginTop: '4px' }}>
                                Don't use your real name
                            </div>
                        </div>
 
                        {/* Email */}
                        <div style={{ marginBottom: '16px' }}>
                            <label style={labelStyle}>Email Address</label>
                            <input
                                type="email" value={email}
                                onChange={e => setEmail(e.target.value)}
                                placeholder="parent@example.com" required
                                style={inputStyle}
                                onFocus={e => { e.target.style.borderColor = C.primary; }}
                                onBlur={e  => { e.target.style.borderColor = C.outlineVariant; }}
                            />
                            {/* Email tip box */}
                            <div style={{
                                marginTop: '8px',
                                background: C.primaryContainer,
                                border: `1.5px solid ${C.primary}`,
                                borderRadius: '10px', padding: '12px',
                                fontSize: '0.8rem', color: C.onSurface, lineHeight: '1.55',
                            }}>
                                <div style={{ marginBottom: '6px' }}><strong>💡 For kids:</strong> Ask your parent for their email</div>
                                <div style={{ marginBottom: '6px' }}>
                                    <strong>💡 Multiple kids?</strong> Use parent+kid1@gmail.com, parent+kid2@gmail.com etc. All emails go to the same inbox!
                                </div>
                                <div style={{ fontSize: '0.72rem', color: C.onSurfaceVariant, fontStyle: 'italic', marginBottom: '8px' }}>
                                    Note: Yahoo/AOL don't support this. Use Gmail or create separate accounts.
                                </div>
                                <div style={{ paddingTop: '8px', borderTop: `1px solid ${C.surfaceHigh}` }}>
                                    <strong>👩‍🏫 Teachers:</strong> Use your school email (.edu, .k12.us) for instant classroom access!
                                </div>
                            </div>
                        </div>
 
                        {/* Password */}
                        <div style={{ marginBottom: '16px' }}>
                            <label style={labelStyle}>Password</label>
                            <input
                                type="password" value={password}
                                onChange={e => setPassword(e.target.value)}
                                placeholder="Create password" required
                                style={inputStyle}
                                onFocus={e => { e.target.style.borderColor = C.primary; }}
                                onBlur={e  => { e.target.style.borderColor = C.outlineVariant; }}
                            />
                        </div>
 
                        {/* Confirm password */}
                        <div style={{ marginBottom: '24px' }}>
                            <label style={labelStyle}>Confirm Password</label>
                            <input
                                type="password" value={confirmPassword}
                                onChange={e => setConfirmPassword(e.target.value)}
                                placeholder="Type password again" required
                                style={inputStyle}
                                onFocus={e => { e.target.style.borderColor = C.primary; }}
                                onBlur={e  => { e.target.style.borderColor = C.outlineVariant; }}
                            />
                        </div>
 
                        <button
                            type="submit"
                            disabled={checking}
                            style={primaryBtnStyle}
                            onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                            onMouseLeave={e => { e.currentTarget.style.opacity = '1';   e.currentTarget.style.transform = 'translateY(0)'; }}
                        >
                            {checking ? 'Checking...' : 'Next: Choose Avatar →'}
                        </button>
 
                        <div style={{ textAlign: 'center', fontSize: '0.85rem', color: C.onSurfaceVariant }}>
                            Already have an account?{' '}
                            <button
                                type="button"
                                onClick={() => navigate('/login')}
                                style={{
                                    background: 'none', border: 'none',
                                    color: C.primary, fontWeight: '700',
                                    cursor: 'pointer', fontSize: '0.85rem',
                                    fontFamily: "'Epilogue', sans-serif", padding: 0,
                                }}
                            >
                                Sign In
                            </button>
                        </div>
                    </form>
                )}
 
                {/* ── Step 2: Avatar ───────────────────────────────────────── */}
                {step === 'avatar' && (
                    <div>
                        <p style={{ textAlign: 'center', fontSize: '0.9rem', color: C.onSurfaceVariant, margin: '0 0 20px 0' }}>
                            Pick a fun avatar to represent you!
                        </p>
 
                        <div style={{
                            display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
                            gap: '10px', marginBottom: '24px',
                        }}>
                            {avatars.map(avatar => (
                                <button
                                    key={avatar}
                                    type="button"
                                    onClick={() => { setSelectedAvatar(avatar); setError(''); }}
                                    style={{
                                        fontSize: '2rem', padding: '16px',
                                        border: `2px solid ${selectedAvatar === avatar ? C.primary : C.outlineVariant}`,
                                        borderRadius: '14px',
                                        background: selectedAvatar === avatar ? C.primaryContainer : '#fff',
                                        cursor: 'pointer',
                                        transition: 'all 0.15s',
                                        boxShadow: selectedAvatar === avatar ? `0 0 0 2px ${C.primaryContainer}` : 'none',
                                    }}
                                    onMouseEnter={e => { if (selectedAvatar !== avatar) e.currentTarget.style.borderColor = C.primary; }}
                                    onMouseLeave={e => { if (selectedAvatar !== avatar) e.currentTarget.style.borderColor = C.outlineVariant; }}
                                >
                                    {avatar}
                                </button>
                            ))}
                        </div>
 
                        <button
                            onClick={handleAvatarSubmit}
                            style={primaryBtnStyle}
                            onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                            onMouseLeave={e => { e.currentTarget.style.opacity = '1';   e.currentTarget.style.transform = 'translateY(0)'; }}
                        >
                            Next: Choose Goal →
                        </button>
 
                        <button
                            onClick={() => setStep('account')}
                            style={ghostBtnStyle}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = C.primary; e.currentTarget.style.color = C.primary; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = C.outlineVariant; e.currentTarget.style.color = C.onSurfaceVariant; }}
                        >
                            ← Back
                        </button>
                    </div>
                )}
 
                {/* ── Step 3: Goal ─────────────────────────────────────────── */}
                {step === 'goal' && (
                    <div>
                        <p style={{ textAlign: 'center', fontSize: '0.9rem', color: C.onSurfaceVariant, margin: '0 0 20px 0' }}>
                            What brings you to Scriblish?
                        </p>
 
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
                            {goals.map(goal => (
                                <button
                                    key={goal.id}
                                    type="button"
                                    onClick={() => { setSelectedGoal(goal.id); setError(''); }}
                                    style={{
                                        padding: '16px 20px',
                                        border: `2px solid ${selectedGoal === goal.id ? C.primary : C.outlineVariant}`,
                                        borderRadius: '14px',
                                        background: selectedGoal === goal.id ? C.primaryContainer : '#fff',
                                        cursor: 'pointer', textAlign: 'left',
                                        transition: 'all 0.15s',
                                        display: 'flex', alignItems: 'center', gap: '16px',
                                    }}
                                    onMouseEnter={e => { if (selectedGoal !== goal.id) e.currentTarget.style.borderColor = C.primary; }}
                                    onMouseLeave={e => { if (selectedGoal !== goal.id) e.currentTarget.style.borderColor = C.outlineVariant; }}
                                >
                                    <span style={{ fontSize: '2rem', flexShrink: 0 }}>{goal.emoji}</span>
                                    <div>
                                        <div style={{
                                            fontFamily: "'Epilogue', sans-serif",
                                            fontSize: '1rem', fontWeight: '800',
                                            color: selectedGoal === goal.id ? C.primary : C.onBackground,
                                            marginBottom: '2px',
                                        }}>
                                            {goal.label}
                                        </div>
                                        <div style={{ fontSize: '0.82rem', color: C.onSurfaceVariant }}>
                                            {goal.desc}
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
 
                        <button
                            onClick={handleGoalSubmit}
                            disabled={loading}
                            style={{
                                ...primaryBtnStyle,
                                background: loading
                                    ? C.outlineVariant
                                    : `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryLight} 100%)`,
                                cursor: loading ? 'not-allowed' : 'pointer',
                                boxShadow: loading ? 'none' : `0 4px 14px ${C.primary}40`,
                            }}
                            onMouseEnter={e => { if (!loading) { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
                            onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(0)'; }}
                        >
                            {loading ? 'Creating Account…' : 'Create Account 🎉'}
                        </button>
 
                        <button
                            onClick={() => setStep('avatar')}
                            disabled={loading}
                            style={{ ...ghostBtnStyle, cursor: loading ? 'not-allowed' : 'pointer' }}
                            onMouseEnter={e => { if (!loading) { e.currentTarget.style.borderColor = C.primary; e.currentTarget.style.color = C.primary; } }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = C.outlineVariant; e.currentTarget.style.color = C.onSurfaceVariant; }}
                        >
                            ← Back
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
 
export default Signup;